import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Upload, Search, Loader2 } from 'lucide-react';
import { PaywallModal } from '@/components/paywall/paywall-modal';
import { useHistoryStore } from '@/lib/store/history-store';
import { initiateDeepSearch } from '@/lib/api/deep-search';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SearchPhotoPage() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<string | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isSubscribed, isSubscriptionLoaded, loadSubscriptionFromFirestore, userId, addSearch } = useHistoryStore();

  useEffect(() => {
    document.title = 'Search by Photo - Deepcheck';
  }, []);

  // Load subscription status on mount
  useEffect(() => {
    if (!isSubscriptionLoaded) {
      loadSubscriptionFromFirestore();
    }
  }, [isSubscriptionLoaded, loadSubscriptionFromFirestore]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSearch = async () => {
    if (!preview) return;

    setError(null);

    // Check subscription status from Firestore state
    if (!isSubscribed) {
      setShowPaywall(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the real deep search API (cloud function verifies subscription)
      const result = await initiateDeepSearch(userId, 'Photo Search', 'search-by-photo');

      logAnalyticsEvent('search_start', { type: 'photo' });

      // Store the image in sessionStorage for the results page
      sessionStorage.setItem(`photo-${result.searchId}`, preview);

      // Add to local store with queued status
      addSearch({
        id: result.searchId,
        type: 'search-by-photo',
        query: 'Photo Search',
        status: 'queued',
        progress: 0,
        timestamp: new Date(),
      });

      // Navigate to analyzing page (listener will handle real-time updates)
      navigate(`/analyzing?id=${result.searchId}&type=search-by-photo&query=Photo Search`);
    } catch (error) {
      console.error('Failed to start search:', error);
      setError(error instanceof Error ? error.message : 'Failed to start search');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribe = () => {
    setShowPaywall(false);
    // After subscription, user can try searching again
  };

  const handleClosePaywall = () => {
    setShowPaywall(false);
  };

  return (
    <main className="flex flex-col h-[calc(100vh-60px)] px-4 pb-6">
      <div className="flex flex-col flex-1 w-full max-w-md mx-auto space-y-6 pt-4">
        {/* Header Section */}
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <Search className="w-8 h-8 text-gray-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Search by Photo</h1>
        </div>

        {/* Upload Area - Takes remaining height */}
        <div className="flex-1 w-full min-h-[200px]">
          {preview ? (
            <div className="relative w-full h-full bg-gray-100 rounded-2xl overflow-hidden">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <label
                htmlFor="photo-upload-change"
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-black/70 transition-colors cursor-pointer backdrop-blur-sm"
              >
                Change photo
                <input
                  id="photo-upload-change"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
          ) : (
            <label
              htmlFor="photo-upload"
              className="flex flex-col items-center justify-center w-full h-full border-4 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-gray-400 transition-colors bg-gray-50"
            >
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <span className="text-gray-600 font-medium">Upload a photo</span>
              <span className="text-sm text-gray-500 mt-1">Click to browse</span>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}

        {/* Search Button */}
        <div className="pt-2">
          <Button
            onClick={handleSearch}
            className="w-full h-14 bg-black hover:bg-gray-800 text-white font-semibold text-lg rounded-full"
            disabled={!preview || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              'Search Now'
            )}
          </Button>
        </div>
      </div>

      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywall}
        onClose={handleClosePaywall}
        onSubscribe={handleSubscribe}
      />
    </main>
  );
}
